/*
 * ── Event Context ──
 *
 * Lightweight shared context that tracks which event the user is
 * currently working on. This connects all event-related subsystems
 * (staff, vendors, budgets, control center) around a single event
 * without requiring each page to re-fetch or re-derive the active event.
 *
 * HOW IT WORKS:
 *   - Event pages (Workspace, Command, ControlCenter) set the context
 *     when they mount and fetch their event data.
 *   - Other pages (Budgets, Staff, Vendor Bookings) can optionally
 *     read the context to know which event is active, but they are
 *     NOT required to — fallback to their own logic if context is empty.
 *   - Context persists across navigation so the "last viewed event"
 *     is always available (e.g. for breadcrumbs, quick-return links).
 *   - Cleared explicitly via clearCurrentEvent() or when user navigates
 *     to a new event (automatically replaced).
 *
 * USAGE:
 *   const { currentEventId, currentEvent, setCurrentEvent } = useEventContext();
 *
 *   // In an event page that already fetches event data:
 *   useEffect(() => {
 *     if (eventData) setCurrentEvent(eventData.eventId, eventData);
 *   }, [eventData]);
 *
 *   // In a list page that wants to highlight the active event:
 *   const { currentEventId } = useEventContext();
 *   const isActive = budget.eventId === currentEventId;
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const EventContext = createContext(null);

export function EventProvider({ children }) {
  const [currentEventId, setCurrentEventId] = useState(null);
  const [currentEvent, setCurrentEventData] = useState(null);

  const setCurrentEvent = useCallback((eventId, eventInfo = null) => {
    setCurrentEventId(eventId || null);
    setCurrentEventData(
      eventInfo
        ? {
            eventId: eventInfo.eventId || eventId,
            name: eventInfo.fullName || eventInfo.name || eventInfo.eventName || '',
            eventType: eventInfo.eventType || '',
            eventDate: eventInfo.eventDate || eventInfo.date || '',
            venue: eventInfo.venue || eventInfo.location || '',
            pipelineStage: eventInfo.pipelineStage || '',
          }
        : null
    );
  }, []);

  const clearCurrentEvent = useCallback(() => {
    setCurrentEventId(null);
    setCurrentEventData(null);
  }, []);

  return (
    <EventContext.Provider
      value={{
        currentEventId,
        currentEvent,
        setCurrentEvent,
        clearCurrentEvent,
      }}
    >
      {children}
    </EventContext.Provider>
  );
}

export function useEventContext() {
  const ctx = useContext(EventContext);
  // Fallback: if used outside provider, return safe defaults
  if (!ctx) {
    return {
      currentEventId: null,
      currentEvent: null,
      setCurrentEvent: () => {},
      clearCurrentEvent: () => {},
    };
  }
  return ctx;
}
